import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react"
import { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions"
import {
  RootState,
  dynamicMiddlewaresInstance,
  registerReducer,
} from "@core/store"
import { baseQueryWithReauth } from "@/modules/app/store"
import { GuestBot } from "../interfaces"

const tagTypes = [
  "Bots",
  "Bot",
  "Templates",
  "Logs",
  "Bindings",
  "ProfilePhoto",
  "JoinJobs",
  "Chats",
  "Mailings",
  "Mailing",
  "Provisions",
] as const
const reducerPath = "guestBotApi"

const baseTGStatApiURL = new URL(
  import.meta.env.REACT_APP_TGSTAT_API,
  window.location.origin,
).toString()
const normalizedBase = baseTGStatApiURL.endsWith("/")
  ? baseTGStatApiURL
  : baseTGStatApiURL + "/"
export const baseUrl = `${normalizedBase}telegram/guest-bots`

const baseQuery: BaseQueryFn = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token
    if (token) headers.set("authorization", `Bearer ${token}`)
    return headers
  },
})

type Builder = EndpointBuilder<
  typeof baseQuery,
  (typeof tagTypes)[number],
  typeof reducerPath
>

const endpoints = (build: Builder) => ({
  botsList: build.query<GuestBot.BotsListRes, GuestBot.BotsListReq | void>({
    query: (req) => ({
      url: ``,
      method: "GET",
      params: { limit: 50, offset: 0, ...req?.queryParams },
    }),
    providesTags: ["Bots"],
  }),
  getBot: build.query<GuestBot.GetBotRes, GuestBot.GetBotReq>({
    query: ({ pathParams: { id } }) => ({ url: `/${id}`, method: "GET" }),
    providesTags: ["Bot"],
  }),
  createBot: build.mutation<GuestBot.CreateBotRes, GuestBot.CreateBotReq>({
    query: ({ body }) => ({ url: ``, method: "POST", body }),
    invalidatesTags: ["Bots"],
  }),
  patchBot: build.mutation<GuestBot.PatchBotRes, GuestBot.PatchBotReq>({
    query: ({ pathParams: { id }, body }) => ({
      url: `/${id}`,
      method: "PATCH",
      body,
    }),
    invalidatesTags: ["Bots", "Bot"],
  }),
  deleteBot: build.mutation<void, GuestBot.DeleteBotReq>({
    query: ({ pathParams: { id } }) => ({ url: `/${id}`, method: "DELETE" }),
    invalidatesTags: ["Bots"],
  }),
  updateProfile: build.mutation<
    GuestBot.UpdateProfileRes,
    GuestBot.UpdateProfileReq
  >({
    query: ({ pathParams: { id }, body }) => ({
      url: `/${id}/profile`,
      method: "PATCH",
      body,
    }),
    invalidatesTags: ["Bots", "Bot"],
  }),
  refreshProfile: build.mutation<
    GuestBot.RefreshProfileRes,
    GuestBot.RefreshProfileReq
  >({
    query: ({ pathParams: { id } }) => ({
      url: `/${id}/profile/refresh`,
      method: "POST",
    }),
    invalidatesTags: ["Bots", "Bot"],
  }),
  setProfilePhoto: build.mutation<
    GuestBot.SetProfilePhotoRes,
    GuestBot.SetProfilePhotoReq
  >({
    query: ({ pathParams: { id }, body }) => ({
      url: `/${id}/profile-photo`,
      method: "PUT",
      body,
    }),
    invalidatesTags: ["ProfilePhoto"],
  }),
  getProfilePhoto: build.query<
    GuestBot.GetProfilePhotoRes,
    GuestBot.GetProfilePhotoReq
  >({
    query: ({ pathParams: { id } }) => ({
      url: `/${id}/profile-photo`,
      method: "GET",
    }),
    providesTags: ["ProfilePhoto"],
  }),
  templatesList: build.query<
    GuestBot.TemplatesListRes,
    GuestBot.TemplatesListReq | void
  >({
    query: (req) => ({
      url: `/templates`,
      method: "GET",
      params: { limit: 50, offset: 0, ...req?.queryParams },
    }),
    providesTags: ["Templates"],
  }),
  createTemplate: build.mutation<
    GuestBot.TemplateRes,
    GuestBot.CreateTemplateReq
  >({
    query: ({ body }) => ({ url: `/templates`, method: "POST", body }),
    invalidatesTags: ["Templates"],
  }),
  updateTemplate: build.mutation<
    GuestBot.TemplateRes,
    GuestBot.UpdateTemplateReq
  >({
    query: ({ pathParams: { id }, body }) => ({
      url: `/templates/${id}`,
      method: "PATCH",
      body,
    }),
    invalidatesTags: ["Templates", "Bot", "Bots"],
  }),
  deleteTemplate: build.mutation<void, GuestBot.DeleteTemplateReq>({
    query: ({ pathParams: { id } }) => ({
      url: `/templates/${id}`,
      method: "DELETE",
    }),
    invalidatesTags: ["Templates"],
  }),
  setResponses: build.mutation<GuestBot.GetBotRes, GuestBot.SetResponsesReq>({
    query: ({ pathParams: { id }, body }) => ({
      url: `/${id}/responses`,
      method: "PUT",
      body,
    }),
    invalidatesTags: ["Bot", "Bots", "Bindings"],
  }),
  deleteResponse: build.mutation<void, GuestBot.DeleteResponseReq>({
    query: ({ pathParams: { id, responseId } }) => ({
      url: `/${id}/responses/${responseId}`,
      method: "DELETE",
    }),
    invalidatesTags: ["Bot", "Bindings"],
  }),
  bindingsList: build.query<
    GuestBot.BindingsListRes,
    GuestBot.BindingsListReq | void
  >({
    query: (req) => ({
      url: `/bindings`,
      method: "GET",
      params: { limit: 50, offset: 0, ...req?.queryParams },
    }),
    providesTags: ["Bindings"],
  }),
  createBinding: build.mutation<
    GuestBot.CreateBindingRes,
    GuestBot.CreateBindingReq
  >({
    query: ({ body }) => ({ url: `/bindings`, method: "POST", body }),
    invalidatesTags: ["Bindings", "Bot", "Bots"],
  }),
  logsList: build.query<GuestBot.LogsListRes, GuestBot.LogsListReq>({
    query: ({ queryParams } = {}) => ({
      url: `/logs`,
      method: "GET",
      params: { limit: 50, offset: 0, ...queryParams },
    }),
    providesTags: ["Logs"],
  }),
  botLogsList: build.query<GuestBot.LogsListRes, GuestBot.BotLogsListReq>({
    query: ({ pathParams: { id }, queryParams }) => ({
      url: `/${id}/logs`,
      method: "GET",
      params: { limit: 50, offset: 0, ...queryParams },
    }),
    providesTags: ["Logs"],
  }),
  createJoinJob: build.mutation<
    GuestBot.CreateJoinJobRes,
    GuestBot.CreateJoinJobReq
  >({
    query: ({ body }) => ({ url: `/chats/join`, method: "POST", body }),
    // The pool grows — invalidate both the job list and the pool.
    // Join writes no journal rows — deliberately no "Logs" here (delta §3).
    invalidatesTags: ["JoinJobs", "Chats"],
  }),
  joinJobsList: build.query<GuestBot.JoinJobsListRes, GuestBot.JoinJobsListReq>(
    {
      query: ({ queryParams } = {}) => ({
        url: `/chats/join-jobs`,
        method: "GET",
        params: { limit: 50, offset: 0, ...queryParams },
      }),
      providesTags: ["JoinJobs"],
    },
  ),
  getJoinJob: build.query<GuestBot.GetJoinJobRes, GuestBot.GetJoinJobReq>({
    query: ({ pathParams: { jobId } }) => ({
      url: `/chats/join-jobs/${jobId}`,
      method: "GET",
    }),
    // No pollingInterval here — polling is configured at the call site
    // (joinJobProgress), so only the progress view polls.
    providesTags: ["JoinJobs"],
  }),
  cancelJoinJob: build.mutation<
    GuestBot.CancelJoinJobRes,
    GuestBot.CancelJoinJobReq
  >({
    query: ({ pathParams: { jobId } }) => ({
      url: `/chats/join-jobs/${jobId}/cancel`,
      method: "POST",
    }),
    // Cancel never removes joined pool rows — no "Chats" here.
    invalidatesTags: ["JoinJobs"],
  }),
  chatsList: build.query<GuestBot.ChatsListRes, GuestBot.ChatsListReq>({
    query: ({ queryParams } = {}) => ({
      url: `/chats`,
      method: "GET",
      params: { limit: 50, offset: 0, ...queryParams },
    }),
    providesTags: ["Chats"],
  }),
  botChats: build.query<GuestBot.BotChatsRes, GuestBot.BotChatsReq>({
    query: ({ pathParams: { id }, queryParams }) => ({
      url: `/${id}/chats`,
      method: "GET",
      params: { limit: 50, offset: 0, ...queryParams },
    }),
    providesTags: ["Chats"],
  }),
  createMailing: build.mutation<
    GuestBot.CreateMailingRes,
    GuestBot.CreateMailingReq
  >({
    query: ({ pathParams: { id }, body }) => ({
      url: `/${id}/mailings`,
      method: "POST",
      body,
    }),
    // The mailing stage writes journal rows — kill any stale cached logs (F8).
    invalidatesTags: ["Mailings", "Mailing", "Logs"],
  }),
  mailingsList: build.query<GuestBot.MailingsListRes, GuestBot.MailingsListReq>(
    {
      query: ({ queryParams } = {}) => ({
        url: `/mailings`,
        method: "GET",
        params: { limit: 50, offset: 0, ...queryParams },
      }),
      providesTags: ["Mailings"],
    },
  ),
  getMailing: build.query<GuestBot.GetMailingRes, GuestBot.GetMailingReq>({
    query: ({ pathParams: { id, mailingId } }) => ({
      url: `/${id}/mailings/${mailingId}`,
      method: "GET",
    }),
    // No pollingInterval here — polling is configured at the call site
    // (mailingProgress), so only the progress view polls.
    providesTags: ["Mailing"],
  }),
  cancelMailing: build.mutation<
    GuestBot.CancelMailingRes,
    GuestBot.CancelMailingReq
  >({
    query: ({ pathParams: { id, mailingId } }) => ({
      url: `/${id}/mailings/${mailingId}/cancel`,
      method: "POST",
    }),
    // Cancel writes journal-adjacent state transitions — keep Logs fresh (F8).
    invalidatesTags: ["Mailings", "Mailing", "Logs"],
  }),
  provisionCreate: build.mutation<
    GuestBot.ProvisionJob,
    GuestBot.CreateProvisionReq
  >({
    query: ({ body }) => ({ url: `/provision`, method: "POST", body }),
    // Бот появляется в обычном списке гостевых ботов в ходе цепочки.
    invalidatesTags: ["Provisions", "Bots"],
  }),
  provisionList: build.query<
    GuestBot.ProvisionsListRes,
    GuestBot.ProvisionsListReq | void
  >({
    query: (req) => ({
      url: `/provision`,
      method: "GET",
      params: { limit: 100, offset: 0, ...req?.queryParams },
    }),
    providesTags: ["Provisions"],
  }),
})

export const guestBotApi = createApi({
  reducerPath,
  tagTypes,
  baseQuery: baseQueryWithReauth(baseQuery),
  endpoints: (build) => ({ ...endpoints(build) }),
})

dynamicMiddlewaresInstance.addMiddleware(guestBotApi.middleware)
registerReducer(guestBotApi.reducerPath, guestBotApi.reducer)

export const {
  useBotsListQuery,
  useGetBotQuery,
  useCreateBotMutation,
  usePatchBotMutation,
  useDeleteBotMutation,
  useUpdateProfileMutation,
  useRefreshProfileMutation,
  useSetProfilePhotoMutation,
  useGetProfilePhotoQuery,
  useTemplatesListQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useSetResponsesMutation,
  useDeleteResponseMutation,
  useBindingsListQuery,
  useCreateBindingMutation,
  useLogsListQuery,
  useBotLogsListQuery,
  useCreateJoinJobMutation,
  useJoinJobsListQuery,
  useGetJoinJobQuery,
  useCancelJoinJobMutation,
  useChatsListQuery,
  useBotChatsQuery,
  useCreateMailingMutation,
  useMailingsListQuery,
  useGetMailingQuery,
  useCancelMailingMutation,
  useProvisionCreateMutation,
  useProvisionListQuery,
} = guestBotApi
